import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import { Table, Tbody, Tr, Td } from '@patternfly/react-table';
import ExpandableSection from 'components/ExpandableSection';
import { storesContext } from 'stores';

const LiveClassDescription = (): any => {
  const { t } = useTranslation();
  const { liveClassStore, routerStore } = useContext(storesContext);
  const route = routerStore?.route as any;
  const completedPerSkuQuotaAllowed =
    liveClassStore.completedPerSkuQuotaAllowed || 0;
  const { isPartnerScheduling } = liveClassStore;

  useEffect(() => {
    const offeringSlug = route?.params.offeringslug;
    if (offeringSlug) {
      liveClassStore.offeringSlug = offeringSlug;
    }
  }, [route?.params.offeringslug, liveClassStore]);

  const schedulingNotes = isPartnerScheduling
    ? [
        t(
          'Clicking Schedule will enroll the learner in the complete live class, from the start date through the end date.',
        ),
        t(
          'Standard schedule type refers to booking that reserves consecutive days of live classes.',
        ),
        t(
          'Non-standard schedule type runs only on specific days between the start and end date time.',
        ),
        t(
          'Cancellations are accepted up to two weeks before the session’s start date.',
        ),
        t(
          "Missed classes without timely cancellation will count toward the learner's allotted class attendance limit for this course.",
        ),
      ]
    : [
        t(
          'Clicking Schedule will enroll you in the complete live class, from the start date through the end date.',
        ),
        t(
          'Standard schedule type refers to booking that reserves consecutive days of live classes.',
        ),
        t(
          'Non-standard schedule type runs only on specific days between the start and end date time.',
        ),
        t(
          'Each live class begins at the same time each day, unless specifically noted by your instructor.',
        ),
        t(
          'Your subscription allows you to attend this live virtual class no more than {{completedPerSkuQuotaAllowed}} times.',
          { completedPerSkuQuotaAllowed },
        ),
        t(
          'Cancelling or rescheduling a class is accepted up to two weeks before the class start date.',
        ),
        t(
          'Missing your scheduled class without timely cancellation will count towards your allotted class attendance limit for this course',
        ),
      ];

  const importantNotes = isPartnerScheduling
    ? [
        t(
          'Please be aware that the labs for your live virtual class are separate from your self paced labs.',
        ),
      ]
    : [
        t(
          'Please be aware that the labs for your live virtual class are separate from your self paced labs.',
        ),
        t(
          'You must start and utilize your class labs to receive instructor assistance during your event.',
        ),
        t(
          'Lab environments should be launched and created at least 30 minutes prior to class start time to ensure that the systems are ready for use and available at the class start.',
        ),
        t('Your labs for this class will be available from the class page.'),
        t(
          "The class page is only accessible once you've scheduled your individual class.",
        ),
        t(
          'Access the class page by clicking View from your live events pane on your home page.',
        ),
        t(
          'Labs can only be started during the class week, and will only be available until the class end date.',
        ),
      ];

  return (
    <div className="live-class-description">
      <ExpandableSection
        localStorageStateKey="LiveVirtualClassDescriptionCollapseState"
        title={liveClassStore.getCourseTitle || ''}
        color="light"
      >
        <div className="pf-m-grid">
          <Table aria-label="Simple table">
            <Tbody>
              <Tr>
                <Td>{t('Scheduling')}</Td>
                <Td>
                  <ul>
                    {schedulingNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </Td>
              </Tr>
              <Tr className="odd-row-class">
                <Td>{t('Important notes')}</Td>
                <Td>
                  <ul>
                    {importantNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </div>
      </ExpandableSection>
    </div>
  );
};

export default withErrorBoundary(
  observer(LiveClassDescription),
  ComponentError,
  handleError,
);
